import type { TMeta } from '@alfa-bank/site-form-helpers/src/meta/models';
import { useMemo } from 'react';
import { useField } from 'react-final-form';

import type { IUseDefaultEmbossingName } from '../models/util-types';

import { MAP, lengthBetweenNames, lengthBeforeLastName } from './constants';

/** Делает транслитерацию с русского на английский */
export const formatToEng = (name: string): string =>
    name
        .toLowerCase()
        .split('')
        .map((char: string) => {
            return MAP[char] || char;
        })
        .join('')
        .trim()
        .toUpperCase()
        .replace(/[^a-z\s-.\d]/gi, '');

export const isSuccess = (
    { dirtySinceLastSubmit, error, submitError, touched }: TMeta,
    firstName: string,
    lastName: string
) => {
    if (!touched && (!firstName || !lastName)) {
        return undefined;
    }
    if (error) return false;
    if (submitError) return dirtySinceLastSubmit;

    return true;
};

export const useName = (name: string) =>
    useField(name, { subscription: { value: true } }).input.value ?? '';

export const validateSymbols = (name: string): string => name.replace(/[^a-z\s-.]/gi, '');

export const deleteStartSymbol = (name: string): string => name.replace(/^[\s.-]+/, '');

export const deleteEndSymbol = (name: string): string => name.replace(/[\s.-]+$/, '');

// Добавляет пробел между точкой и буквой
export const addSpace = (name: string): string => name.replace(/(\.(?=[A-Z]))/g, '. ');

// Запрещает ввод двух подряд символов (точки, тире и запятой), кроме пробела после точки
export const deleteDoubleSymbols = (name: string): string => {
    return name
        .replace(/\.[.-]+/g, '.')
        .replace(/-[.-\s]+/g, '-')
        .replace(/\s[.-\s]+/g, ' ');
};

export const useDefaultEmbossingName = ({
    firstName,
    lastName,
    maxLength,
}: IUseDefaultEmbossingName): string | undefined => {
    const engFirstName = useMemo(() => formatToEng(firstName), [firstName]);
    const engLastName = useMemo(() => formatToEng(lastName), [lastName]);

    const isFullName =
        !maxLength || engFirstName.length + engLastName.length + lengthBetweenNames <= maxLength;

    if (isFullName) {
        const autoName = `${engFirstName} ${engLastName}`.trim();

        return autoName || undefined;
    }

    if (engLastName.length + lengthBeforeLastName <= maxLength) {
        return deleteStartSymbol(`${engFirstName[0]}. ${engLastName}`);
    }

    return engLastName.slice(0, maxLength);
};
